import { LucaIcon, TextTooltip } from '@components'
import { NotificationFeedPopup } from '@containers/NotificationFeed'
import { useLocalStorage, useQueryParam } from '@hooks'
import { isBusinessBillingLocked, useCurrentUser } from '@query'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaBars, FaHeadset } from 'react-icons/fa'
import useScreen from '../../utils/useScreen'
import { AvatarMenu } from './AvatarMenu'
import {
  AIHelpButton,
  BookDemoBg,
  HamburgerButton,
  HeaderSpacer,
  HeaderTitle,
  HeaderTitleLink,
  RightMenuWrapper,
  StyledBookDemoButton,
  StyledHeader
} from './Header.styled'
import cn from 'classnames'
import { useAIHelpContext, useBusinessContext } from '@context'
import { useIsAuthenticated } from '@root/context/AuthContext'
import { bookDemoDismissedCacheKey } from '@root/utils'
import dayjs from 'dayjs'

interface HeaderProps {
  label: string | React.ReactNode
  /** When set, the title is a router link (same bar styling as Documents / Accounts list headers). */
  labelTo?: string
  labelStyle?: Record<string, any>
}

export const Header: React.FC<HeaderProps> = ({ label, labelTo, labelStyle }) => {
  const [t] = useTranslation()
  const [, setMenuOpen] = useQueryParam<string>('menu')
  const { data: business, slug } = useBusinessContext()
  const {
    isInitialized,
    isAIHelpOpen,
    setAIHelpOpen,
    setAllBusinesses,
    setPendingConversationId,
    setWidgetBusinessSlugOverride
  } = useAIHelpContext()
  const isAuthenticated = useIsAuthenticated()
  const isBillingLocked = isBusinessBillingLocked(business)
  const { data: user } = useCurrentUser()

  const { isMobile } = useScreen()
  const showMobileMenu = isMobile && (Boolean(slug) || Boolean(user?.is_agency_admin))

  useEffect(() => {
    if (isBillingLocked) {
      setAIHelpOpen(false)
    }
  }, [isBillingLocked, setAIHelpOpen])

  const handleAIHelpClick = () => {
    if (!isAIHelpOpen) {
      setPendingConversationId(null)
      setWidgetBusinessSlugOverride(null)
      setAllBusinesses(!slug)
    }
    setAIHelpOpen(!isAIHelpOpen)
  }

  return (
    <StyledHeader className={cn({ isMobile })}>
      {showMobileMenu && (
        <HamburgerButton onClick={() => setMenuOpen('true')}>
          <FaBars />
        </HamburgerButton>
      )}

      {labelTo ? (
        <HeaderTitleLink to={labelTo} style={labelStyle}>
          <FaArrowLeft aria-hidden />
          <span>{label}</span>
        </HeaderTitleLink>
      ) : (
        <HeaderTitle style={labelStyle}>{label}</HeaderTitle>
      )}

      <HeaderSpacer />

      {/* ACTIONS */}
      {isAuthenticated && (
        <RightMenuWrapper>
          {!isBillingLocked && <BookDemoButton isMobile={isMobile} />}

          {!isBillingLocked && <NotificationFeedPopup />}
          <AvatarMenu />

          {isInitialized && !isBillingLocked && !isAIHelpOpen && (
            <AIHelpButton
              onClick={handleAIHelpClick}
              className={cn({ isMobile })}
              icon={<LucaIcon />}
              size="sm"
            >
              {t('aiHelp.header.title')}
            </AIHelpButton>
          )}
        </RightMenuWrapper>
      )}
    </StyledHeader>
  )
}

const BUSINESS_AGE_LIMIT_DAYS = 14

const BookDemoButton: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const [t] = useTranslation()
  const { data: business, slug } = useBusinessContext()
  const dismissKey = slug ? bookDemoDismissedCacheKey(slug) : ''
  const [dismissed, setDismissed] = useLocalStorage(dismissKey, false)

  if (!business || !slug || dismissed) return null

  const isNewBusiness = dayjs(business.created_at).isAfter(
    dayjs().subtract(BUSINESS_AGE_LIMIT_DAYS, 'day')
  )
  if (!isNewBusiness) return null

  const handleClick = () => {
    window.open(t('bookDemo.url'), '_blank', 'noopener,noreferrer')
    setDismissed(true)
  }

  return (
    <TextTooltip tooltip={t('bookDemo.tooltip')} placement="bottom-end">
      <StyledBookDemoButton onClick={handleClick} className={cn({ isMobile })}>
        <BookDemoBg />
        <FaHeadset />
        <span>{t('bookDemo.buttonLabel')}</span>
      </StyledBookDemoButton>
    </TextTooltip>
  )
}
